<template>
  <div class="safeguard" :class="{ open: isOpen }">
    <div class="safeguard-title">
      <div class="checkbox-container">
        <input
          type="checkbox"
          :id="safeguard.id"
          :checked="isSafeguardSelected"
          @change="toggleSafeguardChecked"
        />
        <label class="c-checkbox" :for="safeguard.id">
          Safeguard {{ `${safeguard.number?.value}: ${safeguard.title?.value}` }}
        </label>
      </div>
      <button class="safeguard-trigger" @click="toggleDetails(safeguard.id)"></button>
    </div>
    <div class="safeguard-content">
      <div class="safeguard-description">{{ `${safeguard.description?.value}` }}</div>
      <div class="igs">
        <span class="ig one" v-if="safeguard.ig?.numberValue == 1">IG1</span>
        <span class="ig two" v-if="safeguard.ig?.numberValue <= 2">IG2</span>
        <span class="ig three" v-if="safeguard.ig?.numberValue <= 3">IG3</span>
      </div>
      <template v-if="safeguardMappings && safeguardMappings.length > 0">
        <hr />
        <div class="safeguard-label">MAPPINGS</div>
        <div class="mapping" v-for="map in safeguardMappings" :key="map.id">
          <h3 class="mapping__title">{{ map.title?.value }}</h3>
          <div class="mapping__elements" v-for="element in map.mappingElements" :key="element.id">
            <div class="mapping__code">{{ element.code?.value }}</div>
            <div class="mapping__description">
              {{ element.title?.value }}
              <span v-if="element.title?.value.length > 0 && element.description?.value.length > 0"
                >-</span
              >
              {{ element.description?.value }}
            </div>
          </div>
        </div>
      </template>
      <button class="button-collapse" @click="toggleDetails(safeguard.id)">Collapse</button>
      <hr />
    </div>
  </div>
</template>

<script>
export default {
  name: 'CIS-Controls-Navigator-Listing-Safeguard',
  props: {
    safeguard: {
      type: Object,
      default: () => {}
    },
    isSafeguardSelected: {
      type: Boolean,
      default: () => true
    },
    getSafeguardMappings: {
      type: Function,
      default: () => []
    }
  },
  data: function () {
    return {
      isOpen: false,
      safeguardMappings: []
    };
  },
  emits: ['toggleSafeguardChecked'],
  methods: {
    toggleDetails(id) {
      this.isOpen = !this.isOpen;
      // Set the safeguardMappings to empty array when isOpen === false to
      // so the mapping elements are removed from the DOM when not visible.
      const mappings = this.isOpen ? this.getSafeguardMappings(id) : [];
      this.safeguardMappings = mappings ?? [];
    },
    toggleSafeguardChecked() {
      this.$emit('toggleSafeguardChecked', this.safeguard);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/components/checkbox.scss';

.safeguard {
  &:last-child {
    .safeguard-title {
      border-bottom: none;
    }
  }
  &.open {
    .safeguard-content {
      display: block;
    }
    .safeguard-trigger {
      &:before {
        transform: translate(-0.5rem, 0.2rem) rotate(-135deg);
      }
    }
  }

  &-title {
    display: flex;
    justify-content: space-between;
    border-bottom: solid 0.1rem $divider;
    position: relative;

    @include tablet-up {
      border-bottom: none;
    }

    label {
      display: inline-block;
      @include font-styles('Arial', 1.6rem, 500, normal, 2.2rem, 0, $blue);
      margin-top: 1.5rem;
      margin-bottom: 1.5rem;
      padding-right: 8.5rem;
      cursor: pointer;

      @include tablet-up {
        font-size: 2.4rem;
        line-height: 3rem;
        margin-top: 0;
        margin-bottom: 2.5rem;
        padding-right: 9.5rem;
      }
    }

    .checkbox-container {
      padding-left: 0;

      [type='checkbox']:not(:checked) + label:before,
      [type='checkbox']:checked + label:before {
        left: -4rem;
      }

      [type='checkbox']:not(:checked) + label:after,
      [type='checkbox']:checked + label:after {
        left: -3.5rem;
      }
    }
  }

  &-trigger {
    background: transparent;
    border: none;
    position: absolute;
    width: calc(100% - 4rem);
    height: calc(100% - 2.5rem);
    margin-top: 1rem;
    margin-left: 4rem;
    padding: 0;

    @include tablet-up {
      margin-top: 0;
    }

    &:before {
      position: absolute;
      top: 1.5rem;
      right: 0;
      pointer-events: none;
      content: '';
      border: solid $blue;
      border-width: 0 0.2rem 0.2rem 0;
      display: inline-block;
      padding: 0.5rem;
      transform: translate(-0.5rem, -0.5rem) rotate(45deg);
    }
  }

  &-content {
    display: none;
    margin-bottom: 2rem;
  }

  &-label {
    @include font-styles('Arial Narrow', 1.4rem, 600, normal, 1.7rem, 0.1rem, $dark-blue);
    margin-top: 2rem;
  }

  &-description {
    line-height: 2.6rem;
    font-weight: 200;
    margin-top: 2rem;

    @include tablet-up {
      margin-top: 0;
    }
  }

  .button-collapse {
    margin: 0 auto;
    margin-bottom: 2rem;
    padding-right: 3rem;
    background: transparent;
    border: none;
    position: relative;
    display: inherit;
    text-transform: uppercase;
    @include font-styles('Arial Narrow', 1.8rem, 600, normal, 2.2rem, 0, $blue);

    &:before {
      position: absolute;
      top: 50%;
      right: 0;
      pointer-events: none;
      content: '';
      border: solid $blue;
      border-width: 0 0.2rem 0.2rem 0;
      display: inline-block;
      padding: 0.5rem;
      transform: translate(-50%, -23%) rotate(-135deg);
    }
  }

  .mapping {
    margin-top: 2.5rem;

    &__title {
      @include font-styles('Arial', 2rem, 600, normal, 2.6rem, 0, $dark-blue);
    }

    &__elements {
      border-left: solid 0.5rem $orange;
      padding-left: 2.6rem;
    }

    &__code {
      @include font-styles('Arial', 2rem, 200, normal, 2.6rem, 0, $dark-blue);
    }

    &__description {
      @include font-styles('Arial', 2rem, 200, normal, 2.6rem, 0, $gray);
      margin-bottom: 2.8rem;
    }
  }
}
.igs {
  margin: 2.5rem 0;

  .ig {
    display: inline-block;
    width: 5.7rem;
    padding: 0.5rem 0 0.6rem 0;
    border: 0.2rem solid;
    border-radius: 0.5rem;
    margin-right: 1.2rem;
    text-align: center;
    @include font-styles('Arial', 1.4rem, 500, normal, 2rem, 0, $dark-blue);

    &.one {
      border-color: #73ae57;
    }

    &.two {
      border-color: #e28f37;
    }

    &.three {
      border-color: #4eacbb;
    }
  }
}
</style>
